import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getRelatedVideos, getVideoById } from "@/lib/videos";
import { useFavorites } from "@/hooks/useFavorites";
import { typeLabels } from "@/mocks/filters";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import VideoCard from "@/components/feature/VideoCard";

export default function DetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const video = getVideoById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  usePageMeta({
    title: video ? `${video.title} - CC视频影视详情` : "影视详情 - CC视频",
    description: video
      ? `${video.title}（${video.year}·${video.region}·${video.genres.join("、")}）剧情简介与相关推荐。${video.summary}`
      : "CC视频影视详情页。",
    keywords: video
      ? `${video.title},${typeLabels[video.type]},${video.genres.join(",")},CC视频影视详情`
      : "CC视频影视详情",
  });

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 2000);
    return () => clearTimeout(timer);
  }, [toast]);

  if (!video) {
    return (
      <div className="max-w-content mx-auto px-4 md:px-6 py-16">
        <EmptyState
          icon="ri-movie-2-line"
          title="未找到该影视作品"
          description="作品可能已下架或链接有误，看看首页的精选推荐吧。"
          actionLabel="返回首页"
          onAction={() => navigate("/")}
        />
      </div>
    );
  }

  const favored = isFavorite(video.id);
  const related = getRelatedVideos(video, 4);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: video.title, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setToast("链接已复制到剪贴板");
    } catch {
      setToast("复制失败，请手动复制地址栏链接");
    }
  };

  const infoRows = [
    { label: "类型", value: typeLabels[video.type] },
    { label: "地区", value: video.region },
    { label: "年份", value: String(video.year) },
    { label: "时长", value: video.duration },
    { label: "导演", value: video.director },
    { label: "主演", value: video.cast.join(" / ") },
  ];

  return (
    <div className="max-w-content mx-auto px-4 md:px-6 py-6 md:py-8">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: typeLabels[video.type], to: `/category/${video.type}` },
          { label: video.title },
        ]}
      />

      <div className="mt-5 rounded-lg bg-background-100 p-5 md:p-8">
        <div className="flex flex-col md:flex-row gap-6 md:gap-8">
          <div className="w-44 sm:w-52 md:w-64 shrink-0 mx-auto md:mx-0">
            <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
              <img
                src={video.cover}
                alt={`${video.title} 海报`}
                title={`${video.title} - CC视频影视详情`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
              {video.title}
            </h1>

            <div className="mt-3 flex items-center flex-wrap gap-x-4 gap-y-2">
              <span className="flex items-center gap-1 text-accent-600 font-bold">
                <i className="ri-star-fill"></i>
                {video.rating.toFixed(1)}
                <span className="text-xs font-normal text-foreground-400">/ 10</span>
              </span>
              <span className="flex items-center gap-1 text-sm text-foreground-500">
                <i className="ri-fire-line text-accent-500"></i>
                热度 {video.hot.toLocaleString()}
              </span>
            </div>

            <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {infoRows.map((row) => (
                <div key={row.label} className="flex gap-3 text-sm">
                  <dt className="text-foreground-400 w-12 shrink-0">{row.label}</dt>
                  <dd className="text-foreground-800">{row.value}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-4 flex flex-wrap gap-2">
              {video.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => toggleFavorite(video.id)}
                className={`h-11 px-5 rounded-md text-sm font-medium flex items-center gap-1.5 transition-colors whitespace-nowrap cursor-pointer ${
                  favored
                    ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                    : "bg-background-50 border border-background-300 text-foreground-800 hover:border-accent-400 hover:text-accent-600"
                }`}
              >
                <i className={favored ? "ri-heart-fill" : "ri-heart-line"}></i>
                {favored ? "已收藏" : "收藏"}
              </button>

              <button
                type="button"
                onClick={handleShare}
                className="h-11 px-5 rounded-md bg-background-50 border border-background-300 text-foreground-800 text-sm font-medium flex items-center gap-1.5 hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-share-line"></i>
                分享
              </button>

              <Link
                to={`/category/${video.type}`}
                className="h-11 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium flex items-center gap-1.5 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-apps-2-line"></i>
                浏览{typeLabels[video.type]}频道
              </Link>
            </div>
          </div>
        </div>
      </div>

      <section className="mt-8 md:mt-10">
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 flex items-center gap-2">
          <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
          剧情简介
        </h2>
        <div className="mt-4 space-y-3 text-sm md:text-base text-foreground-700 leading-loose max-w-4xl">
          {video.synopsis.map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>
      </section>

      {related.length > 0 && (
        <section className="mt-10 md:mt-12">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 flex items-center gap-2">
            <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
            相关推荐
          </h2>
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {related.map((item) => (
              <VideoCard key={item.id} video={item} />
            ))}
          </div>
        </section>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-full bg-foreground-950 text-background-50 text-sm ccv-fade">
          {toast}
        </div>
      )}
    </div>
  );
}